import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PenSquare, Pencil, ExternalLink, Eye, EyeOff, Trash2 } from 'lucide-react';
import { get, patch, del } from '@/api/client';
import { confirmModal } from '@/store/confirmStore';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { toast } from '@/store/toastStore';
import { cn } from '@/lib/cn';

/**
 * 어드민 — 이벤트 관리.
 * - GET /events?filter=all 로 진행 중 + 종료 모두 조회 (admin 권한 무관, 공개 API).
 * - 작성/수정은 기존 WritePage 재사용 (`/write?board=event` 와 `/write?edit=:post_id`).
 *   진정한 어드민 레이아웃 내부 편집기는 추후 EventForm 컴포넌트 추출 시점에 도입.
 */

type EventFilter = 'active' | 'ended' | 'all';

interface AdminEventRow {
  id: number;
  title: string;
  description: string;
  starts_at: string;
  ends_at: string;
  reward_points: number;
  image_url: string | null;
  is_active: boolean;
  /** 공개 여부 — false 면 어드민만 조회 가능 */
  is_published: boolean;
  action_type?: string;
  post_id?: number | null;
  is_perpetual?: boolean;
}

export function AdminEventsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<EventFilter>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'events', filter],
    queryFn: () => get<{ events: AdminEventRow[] }>(`/events?filter=${filter}`),
    staleTime: 30_000,
  });

  /** 이벤트 hard delete — DELETE /events/:id (post 연결 시 posts 도 함께 삭제됨) */
  const deleteMut = useMutation({
    mutationFn: (id: number) => del<{ id: number; deletedPostId: number | null }>(`/events/${id}`),
    onSuccess: () => {
      toast.success('이벤트가 삭제되었어요.');
      qc.invalidateQueries({ queryKey: ['admin', 'events'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    },
    onError: () => {
      toast.error('삭제에 실패했어요.');
    },
  });

  /** 공개 토글 — PATCH /events/:id/publish 호출 후 캐시 무효화 */
  const publishMut = useMutation({
    mutationFn: (vars: { id: number; isPublished: boolean }) =>
      patch<{ id: number; isPublished: boolean }>(`/events/${vars.id}/publish`, {
        isPublished: vars.isPublished,
      }),
    onSuccess: (res) => {
      toast.success(res.isPublished ? '이벤트를 공개했어요.' : '이벤트를 비공개로 전환했어요.');
      qc.invalidateQueries({ queryKey: ['admin', 'events'] });
      qc.invalidateQueries({ queryKey: ['events'] }); // 사용자 페이지도 동기화
    },
    onError: () => {
      toast.error('공개 상태 변경에 실패했어요.');
    },
  });

  const events = data?.events ?? [];

  return (
    <div className="w-full">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold">이벤트 관리</h1>
          <p className="mt-1 text-xs text-[var(--color-text-sub)]">
            새 이벤트 작성과 기존 이벤트 수정/조기 종료를 여기서 처리해요.
            (사용자 페이지의 이벤트 작성 버튼은 제거됨)
          </p>
        </div>
        <Button size="md" onClick={() => navigate('/admin/events/new')}>
          <PenSquare className="h-4 w-4" />
          새 이벤트 작성
        </Button>
      </header>

      {/* 필터 탭 */}
      <div className="mb-5 flex w-full gap-1 rounded-md bg-[var(--color-tab-bg)] p-1">
        {([
          { key: 'all',    label: '전체' },
          { key: 'active', label: '진행 중' },
          { key: 'ended',  label: '종료' },
        ] as const).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setFilter(t.key)}
            className={cn(
              'flex-1 rounded-md px-3 py-1.5 text-sm transition-colors',
              filter === t.key
                ? 'bg-[var(--color-tab-active)] font-semibold text-[var(--color-text)]'
                : 'text-[var(--color-text-sub)] hover:bg-[var(--color-tab-hover)]',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 목록 */}
      {isLoading ? (
        <Skeleton className="h-64" />
      ) : events.length === 0 ? (
        <p className="rounded-md border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-text-sub)]">
          {filter === 'ended' ? '종료된 이벤트가 없어요.' : '등록된 이벤트가 없어요.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {events.map((e) => {
            const now = Date.now();
            const ongoing = e.is_perpetual || new Date(e.ends_at).getTime() > now;
            return (
              <li
                key={e.id}
                className="flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold">{e.title}</span>
                    {/* 공개 여부 — 비공개는 빨간색으로 강조해 운영자 시야에 잘 들어오게 */}
                    {e.is_published ? (
                      <Badge variant="primary">공개</Badge>
                    ) : (
                      <Badge variant="fall">비공개</Badge>
                    )}
                    {ongoing ? (
                      <Badge variant="rise">진행 중</Badge>
                    ) : (
                      <Badge variant="default">종료</Badge>
                    )}
                    {e.is_perpetual && <Badge variant="primary">상시</Badge>}
                    {e.reward_points > 0 && (
                      <Badge variant="editor">🎁 최대 {e.reward_points.toLocaleString('ko-KR')}P</Badge>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-1 text-xs text-[var(--color-text-sub)]">
                    {e.description}
                  </p>
                  <p className="mt-0.5 text-[11px] text-[var(--color-text-sub)] tabular-nums">
                    {e.is_perpetual
                      ? `${e.starts_at.slice(0, 10)} 시작 · 상시`
                      : `${e.starts_at.slice(0, 10)} ~ ${e.ends_at.slice(0, 10)}`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {/* 공개/비공개 토글 — 사용자 페이지 노출 여부 즉시 반영 */}
                  <Button
                    size="sm"
                    variant={e.is_published ? 'ghost' : 'primary'}
                    loading={publishMut.isPending && publishMut.variables?.id === e.id}
                    onClick={() =>
                      publishMut.mutate({ id: e.id, isPublished: !e.is_published })
                    }
                  >
                    {e.is_published ? (
                      <>
                        <EyeOff className="h-3.5 w-3.5" />
                        비공개로
                      </>
                    ) : (
                      <>
                        <Eye className="h-3.5 w-3.5" />
                        공개
                      </>
                    )}
                  </Button>
                  {/* 상세(공개 페이지) — 사용자 시각으로 확인 (어드민이면 비공개도 조회 가능) */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/events/${e.id}`)}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    상세
                  </Button>
                  {/* 수정 — 모든 이벤트(post 연결 여부 무관) 가 어드민 페이지 안에서 처리됨 */}
                  <Button
                    size="sm"
                    onClick={() => navigate(`/admin/events/${e.id}/edit`)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    수정
                  </Button>
                  {/* 삭제 — hard delete (event_entries CASCADE, 연결 post 동반 삭제). 복원 불가 */}
                  <Button
                    size="sm"
                    variant="danger"
                    loading={deleteMut.isPending && deleteMut.variables === e.id}
                    onClick={async () => {
                      const ok = await confirmModal({
                        title: '이벤트 삭제',
                        message: `"${e.title}" 이벤트를 정말 삭제할까요?${
                          e.post_id ? '\n연결된 게시글도 함께 삭제됩니다.' : ''
                        }\n참여 기록도 모두 삭제되며 복원할 수 없어요.`,
                        confirmLabel: '삭제',
                        cancelLabel: '취소',
                        tone: 'danger',
                      });
                      if (ok) deleteMut.mutate(e.id);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    삭제
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

    </div>
  );
}

